import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { AppContext } from '../../../contexts/AppContext';
import axiosInstance from '../../../config/axios';
import { apiGet } from '../../../utils/api';
import { assets } from '../../../assets/assets';
import SelectProjectEmployeesModal from './SelectProjectEmployeesModal';

const EMPTY_FORM = { nom: '', description: '', date_debut: '', date_fin_prevue: '' };

const todayIso = () => new Date().toISOString().split('T')[0];

const toDateInput = (value) => (value ? new Date(value).toISOString().split('T')[0] : '');

/**
 * Secura Web : création de projet en fenêtre modale (même rendu que « Nouveau shift »).
 * Avec `projetId`, la même fenêtre sert à modifier le projet.
 */
const CreateProjectModal = ({ isOpen, onClose, onCreated, projetId = null, onUpdated }) => {
  const isEdit = Boolean(projetId);
  const { userData, interimStatus } = useContext(AppContext);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [department, setDepartment] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDateDebut, setInitialDateDebut] = useState('');
  const [projectDepartmentId, setProjectDepartmentId] = useState(null);

  // Intérimaire (sans être responsable) : le projet appartient au département remplacé.
  const userDepartmentId =
    interimStatus?.isInterim && !userData?.isResponsableDepartement
      ? interimStatus.departmentId
      : userData?.department_id ?? interimStatus?.departmentId;
  const departmentId = isEdit ? projectDepartmentId : userDepartmentId;

  // Modification : préremplir avec le projet existant
  useEffect(() => {
    if (!isOpen || !isEdit) return undefined;
    let cancelled = false;
    setProjectDepartmentId(null);
    (async () => {
      try {
        const response = await axiosInstance.get(`/projets/${projetId}`);
        const projet = response.data?.data || response.data?.projet;
        if (cancelled || !projet) return;
        const dateDebut = toDateInput(projet.date_debut);
        setFormData({
          nom: projet.nom || '',
          description: projet.description || '',
          date_debut: dateDebut,
          date_fin_prevue: toDateInput(projet.date_fin_prevue)
        });
        setInitialDateDebut(dateDebut);
        setSelectedEmployees(
          (projet.assignments || []).map(a => Number(a.employee_id || a.employee?.id)).filter(Boolean)
        );
        setProjectDepartmentId(projet.department_id || userDepartmentId);
      } catch (error) {
        console.error('Erreur chargement projet:', error);
        if (!cancelled) {
          toast.error('Erreur lors du chargement du projet');
          onClose();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isEdit, projetId]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setSelectedEmployees([]);
      setShowEmployeesModal(false);
      return;
    }
    if (isEdit && !departmentId) return; // département connu après chargement du projet
    if (!departmentId) {
      toast.error('Vous n\'êtes pas assigné à un département');
      onClose();
      return;
    }

    let cancelled = false;
    const load = async () => {
      setEmployeesLoading(true);
      try {
        const deptResponse = await apiGet(`/api/departements/${departmentId}`);
        const dept = deptResponse?.data || deptResponse?.department || deptResponse;
        if (!cancelled && dept?.id) setDepartment(dept);

        const employeesResponse = await apiGet(`/api/departements/${departmentId}/employes`);
        const employeesList = Array.isArray(employeesResponse)
          ? employeesResponse
          : (employeesResponse?.employes || employeesResponse?.data || []);
        if (!cancelled) setEmployees(employeesList.filter(emp => emp.statut === 'actif'));
      } catch (error) {
        console.error('Erreur chargement département/employés:', error);
        if (!cancelled) toast.error(error.response?.data?.message || 'Erreur lors du chargement des employés');
      } finally {
        if (!cancelled) setEmployeesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, departmentId]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Date de début postérieure à la date de fin : on vide la date de fin
      ...(name === 'date_debut' && prev.date_fin_prevue && value > prev.date_fin_prevue ? { date_fin_prevue: '' } : {})
    }));
  };

  const selectedEmployeesData = employees.filter(emp =>
    selectedEmployees.some(id => String(id) === String(emp.id || emp.employee_id))
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom.trim()) {
      toast.error('Le nom du projet est obligatoire');
      return;
    }
    // En modification, une date de début déjà passée et inchangée reste acceptée
    if (formData.date_debut && formData.date_debut < todayIso() && !(isEdit && formData.date_debut === initialDateDebut)) {
      toast.error('La date de début ne peut pas être dans le passé');
      return;
    }
    if (formData.date_debut && formData.date_fin_prevue && formData.date_fin_prevue < formData.date_debut) {
      toast.error('La date de fin prévue ne peut pas être antérieure à la date de début');
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        nom: formData.nom.trim(),
        description: formData.description || '',
        date_debut: formData.date_debut || null,
        date_fin_prevue: formData.date_fin_prevue || null,
        department_id: department?.id || departmentId,
        employee_ids: selectedEmployees
      };

      if (isEdit) {
        const response = await axiosInstance.put(`/projets/${projetId}`, payload);
        if (!response.data.success) {
          toast.error(response.data.message || 'Erreur lors de la modification');
          return;
        }
        toast.success('Projet modifié avec succès');
        onClose();
        onUpdated?.(projetId);
        return;
      }

      const response = await axiosInstance.post('/projets', { ...payload, responsable_id: userData.id });

      if (!response.data.success) {
        toast.error(response.data.message || 'Erreur lors de la création');
        return;
      }

      let projetData = response.data.data;
      if (typeof projetData === 'string') {
        try {
          projetData = JSON.parse(projetData);
        } catch {
          projetData = null;
        }
      }

      toast.success('Projet créé avec succès');
      onClose();
      onCreated?.(projetData);
    } catch (error) {
      console.error(isEdit ? 'Erreur modification projet:' : 'Erreur création projet:', error);
      toast.error(error.response?.data?.message || (isEdit ? 'Erreur lors de la modification du projet' : 'Erreur lors de la création du projet'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[10000] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div className="bg-white rounded-2xl shadow-2xl p-4 w-full max-w-sm">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900">{isEdit ? 'Modifier le projet' : 'Nouveau projet'}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {department?.libelle ? (
              <div className="w-full px-4 py-2 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 bg-gray-50 text-center">
                {department.libelle}
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du projet <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className={inputClass}
                placeholder="Ex: Refonte du site web"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={150}
                rows="3"
                className={`${inputClass} resize-none`}
                placeholder="Description du projet..."
              />
              <span className="text-xs text-gray-500">{formData.description.length}/150 caractères</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <input
                  type="date"
                  name="date_debut"
                  value={formData.date_debut}
                  onChange={handleChange}
                  min={isEdit && initialDateDebut && initialDateDebut < todayIso() ? initialDateDebut : todayIso()}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin prévue</label>
                <input
                  type="date"
                  name="date_fin_prevue"
                  value={formData.date_fin_prevue}
                  onChange={handleChange}
                  min={formData.date_debut || todayIso()}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Bouton de sélection des employés */}
            <button
              type="button"
              onClick={() => setShowEmployeesModal(true)}
              className="w-full px-4 py-2 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
            >
              {selectedEmployees.length > 0 ? (
                <>
                  <span>Employés</span>
                  <div className="flex items-center -space-x-2">
                    {selectedEmployeesData.slice(0, 3).map((emp, idx) => {
                      const profileImageUrl = emp.profileImageUrl || emp.imageProfil || emp.image_profil || emp.photo || emp.avatar;
                      return (
                        <div
                          key={emp.id || emp.employee_id || idx}
                          className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
                          style={{ zIndex: 10 - idx }}
                        >
                          <img
                            src={profileImageUrl || assets.employes}
                            alt={emp.nom_complet || 'Employé'}
                            className={`w-full h-full border-2 border-white rounded-full ${profileImageUrl ? 'object-cover' : 'object-contain'}`}
                            onError={(e) => { e.target.src = assets.employes; }}
                          />
                        </div>
                      );
                    })}
                    {selectedEmployees.length > 3 && (
                      <div
                        className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-semibold text-gray-700 bg-gray-100 flex-shrink-0"
                        style={{ borderColor: '#0026E2', zIndex: 0 }}
                      >
                        +{selectedEmployees.length - 3}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                'Sélectionner les employés'
              )}
            </button>

            <div className="mt-5 flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-5 py-2 bg-[#0026E2] text-white rounded-2xl font-semibold hover:bg-[#0018B8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{isEdit ? 'Enregistrement...' : 'Création...'}</span>
                  </>
                ) : (
                  isEdit ? 'Enregistrer' : 'Créer le projet'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de sélection des employés du département */}
      <SelectProjectEmployeesModal
        isOpen={showEmployeesModal}
        onClose={() => setShowEmployeesModal(false)}
        employees={employees}
        loading={employeesLoading}
        selectedEmployees={selectedEmployees}
        onSelectEmployees={setSelectedEmployees}
      />
    </>,
    document.body
  );
};

export default CreateProjectModal;
